import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { useState } from 'react';
import type { ReactNode } from 'react';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

interface SelectProps {
  readonly label: string;
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly onChange: (next: string) => void;
  readonly className?: string;
  readonly id?: string;
}

const SEARCHABLE_FROM = 8;

const FILTER_TRIGGER =
  'flex h-11 w-full items-center gap-2 rounded-field border border-hairline bg-white px-2.5 text-left text-[13px] font-normal text-navy outline-none transition focus:border-royal/55 focus:ring-[3px] focus:ring-royal/15 sm:w-auto dark:border-dark-line dark:bg-dark-field dark:text-dark-ink';

const PANEL =
  'z-50 flex w-[var(--button-width)] min-w-52 flex-col overflow-hidden rounded-panel border border-hairline bg-white shadow-menu [--anchor-gap:4px] [--anchor-max-height:20rem] [--anchor-padding:12px] dark:border-dark-line dark:bg-dark-panel';

const OPTION =
  'cursor-pointer rounded-field px-2.5 py-1.5 text-[13px] font-normal text-navy transition data-focus:bg-mist data-selected:text-royal dark:text-dark-ink dark:data-focus:bg-white/8';

const SEARCH =
  'h-8 w-full rounded-field border border-hairline bg-field px-2.5 text-[13px] text-navy outline-none placeholder:text-muted/70 focus:border-royal/55 focus:bg-white dark:border-dark-line dark:bg-dark-field dark:text-dark-ink';

function Chevron(): ReactNode {
  return (
    <svg
      viewBox="0 0 16 16"
      className="ml-auto size-3.5 shrink-0 text-muted"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m4 6 4 4 4-4" />
    </svg>
  );
}

function OptionList({
  label,
  options,
  searchable,
  multiple = false,
}: {
  readonly label: string;
  readonly options: readonly SelectOption[];
  readonly searchable: boolean;
  readonly multiple?: boolean;
}): ReactNode {
  const [query, setQuery] = useState('');

  const needle = query.trim().toLowerCase();
  const matches =
    needle === ''
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(needle));

  return (
    <>
      {searchable ? (
        <div className="shrink-0 border-b border-hairline px-2 py-2 dark:border-dark-line">
          <input
            autoFocus
            type="text"
            value={query}
            placeholder={`Search ${label.toLowerCase()}…`}
            aria-label={`Search ${label.toLowerCase()}`}
            className={SEARCH}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key.length === 1 || event.key === 'Backspace') {
                event.stopPropagation();
              }
            }}
          />
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto p-1">
        {matches.length === 0 ? (
          <p className="px-2.5 py-2 text-[12.5px] text-muted">Nothing matches</p>
        ) : (
          matches.map((option) => (
            <ListboxOption key={option.value} value={option.value} className={OPTION}>
              {({ selected }) => (
                <span className="flex items-center gap-2">
                  {multiple ? (
                    <span
                      aria-hidden="true"
                      className={`grid size-4 shrink-0 place-items-center rounded-[5px] border ${
                        selected
                          ? 'border-royal bg-royal text-white'
                          : 'border-hairline dark:border-dark-line'
                      }`}
                    >
                      {selected ? (
                        <svg
                          viewBox="0 0 16 16"
                          className="size-3"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3.5 8.5l3 3 6-7" />
                        </svg>
                      ) : null}
                    </span>
                  ) : null}
                  <span className="truncate">{option.label}</span>
                </span>
              )}
            </ListboxOption>
          ))
        )}
      </div>
    </>
  );
}

export function Select({ label, value, options, onChange, className, id }: SelectProps): ReactNode {
  const selectedLabel = options.find((option) => option.value === value)?.label ?? '';

  return (
    <Listbox value={value} onChange={onChange}>
      <ListboxButton id={id} className={className ?? FILTER_TRIGGER} aria-label={label}>
        <span className="truncate">{selectedLabel}</span>
        <Chevron />
      </ListboxButton>

      <ListboxOptions anchor="bottom start" className={PANEL}>
        <OptionList label={label} options={options} searchable={options.length > SEARCHABLE_FROM} />
      </ListboxOptions>
    </Listbox>
  );
}

interface MultiSelectProps {
  readonly label: string;
  readonly value: readonly string[];
  readonly options: readonly SelectOption[];
  readonly onChange: (next: readonly string[]) => void;
  readonly placeholder: string;
  readonly className?: string;
  readonly id?: string;
}

export function MultiSelect({
  label,
  value,
  options,
  onChange,
  placeholder,
  className,
  id,
}: MultiSelectProps): ReactNode {
  const picked = options.filter((option) => value.includes(option.value));

  const summary =
    picked.length === 0
      ? placeholder
      : picked.length === 1
        ? (picked[0]?.label ?? placeholder)
        : `${label}: ${String(picked.length)}`;

  return (
    <Listbox multiple value={[...value]} onChange={onChange}>
      <ListboxButton
        id={id}
        className={`${className ?? FILTER_TRIGGER} ${picked.length > 0 ? 'border-royal/45 text-royal' : ''}`}
        aria-label={label}
      >
        <span className="truncate">{summary}</span>
        <Chevron />
      </ListboxButton>

      <ListboxOptions anchor="bottom start" className={PANEL}>
        <OptionList
          label={label}
          options={options}
          searchable={options.length > SEARCHABLE_FROM}
          multiple
        />
      </ListboxOptions>
    </Listbox>
  );
}
